import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Users,
  Package,
  Book,
  Menu,
  NotepadText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FlaskConical,
} from "lucide-react";
import { TiLeaf } from "react-icons/ti";
import "./dashboard.css";
import Loading from "../loading/Loading";
import LoadingDashboard from "../loading/LoadingDashboard";
import { GlobalContext } from "../../GlobalContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const { userData, isLoading, subscribe } = useContext(GlobalContext);
  const [orders, setOrders] = useState([]);
  const [patients, setPatients] = useState([]);
  const [orderedTests, setOrderedTests] = useState(0);
  const [pendingResults, setPendingResults] = useState(0);
  const [totalPatients, setTotalPatients] = useState(0);
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [ordersError, setOrdersError] = useState(null);
  const [patientsError, setPatientsError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const ORDERS_API_ENDPOINT = "/wp-json/dbsb/v1/orders/list";
  const PATIENTS_API_ENDPOINT = "/wp-json/dbsb/v1/user/patients";

  // Subscribe to user data updates
  useEffect(() => {
    const unsubscribe = subscribe((eventType) => {
      if (eventType === "USER_DATA_UPDATED") {
        // User data is already updated in GlobalContext
      }
    });
    return unsubscribe;
  }, [subscribe]);

  // Fetch orders data
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoadingOrders(true);
      setOrdersError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(
          `${ORDERS_API_ENDPOINT}?per_page=4&page=1`,
          {
            method: "GET",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              "X-WP-Nonce": window.wpApiSettings.nonce,
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("You must be logged in to view orders.");
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view orders.");
          }
          throw new Error("Failed to fetch orders.");
        }

        const data = await response.json();
        const mappedOrders = data.orders.slice(0, 4).map((order) => ({
          orderId: order.order_id.toString(),
          patientFirstName: order.patient_name
            ? order.patient_name.split(" ")[0]
            : "Unknown",
          patientLastName: order.patient_name
            ? order.patient_name.split(" ").slice(1).join(" ")
            : "",
          testName: order.test_name || "Unknown",
          orderDate: new Date(order.date).toLocaleString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
          status: order.status.charAt(0).toUpperCase() + order.status.slice(1),
        }));

        setOrders(mappedOrders);
        setOrderedTests(data.total);
        setPendingResults(data.pending_results);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setOrdersError(error.message);
        setOrders([]);
        setOrderedTests(0);
        setPendingResults(0);
      } finally {
        setIsLoadingOrders(false);
      }
    };

    fetchOrders();
  }, []);

  // Fetch patients data
  useEffect(() => {
    const fetchPatients = async () => {
      if (userData.role !== "provider") {
        setIsLoadingPatients(false);
        return;
      }

      setIsLoadingPatients(true);
      setPatientsError(null);
      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error(
            "Authentication token not found. Please try logging in again."
          );
        }

        const response = await fetch(`${PATIENTS_API_ENDPOINT}?per_page=4`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error("You must be logged in to view patients.");
          } else if (response.status === 403) {
            throw new Error("You don't have permission to view patients.");
          }
          throw new Error("Failed to fetch patients.");
        }

        const data = await response.json();
        const mappedPatients = data.patients.slice(0, 4).map((patient) => ({
          id: patient.id.toString(),
          firstName: patient.first_name || "Unknown",
          lastName: patient.last_name || "",
          email: patient.email || "N/A",
          testsOrdered: patient.published_order_count || 0,
        }));

        setPatients(mappedPatients);
        setTotalPatients(data.total);
      } catch (error) {
        console.error("Error fetching patients:", error);
        setPatientsError(error.message);
        setPatients([]);
        setTotalPatients(0);
      } finally {
        setIsLoadingPatients(false);
      }
    };

    fetchPatients();
  }, [userData.role]);

  // Set initial load to false after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const recentOrders = orders.slice(0, 4);
  const recentPatients = patients.slice(0, 4);

  const statusClasses = {
    Pending: "status-pending",
    Processing: "status-processing",
    Paid: "status-paid",
    Scheduled: "status-scheduled",
    Collected: "status-collected",
    "In transit": "status-in-transit",
    "Test in progress": "status-test-in-progress",
    Testing: "status-testing",
    in_transit: "status-in-transit",
    result_ready: "status-result-ready",
    Cancelled: "status-cancelled",
  };

  // Animation variants
  const fadeIn = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.5 } },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const orderCardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    }),
    hover: {
      y: -5,
      boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 17,
      },
    },
  };

  const patientRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    }),
    hover: {
      backgroundColor: "rgba(58, 63, 106, 0.02)",
      transition: { duration: 0.2 },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 },
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1 },
    },
  };

  const metricCountVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 15,
        delay: 0.3,
      },
    },
  };

  const profilePicVariants = {
    hover: {
      scale: 1.1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 10,
      },
    },
  };

  const thumbprintVariants = {
    hidden: { opacity: 0, rotate: -10 },
    visible: (i) => ({
      opacity: 0.7,
      rotate: 0,
      transition: {
        delay: 0.2 + i * 0.1,
        type: "spring",
        stiffness: 200,
        damping: 20,
      },
    }),
  };

  const headingVariants = {
    hidden: { opacity: 0, y: -15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  return (
    <motion.div
      className="dashboard-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="welcome-quick-actions"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="welcome-card" variants={itemVariants}>
          <motion.div
            className="welcome-text"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Welcome 👋,{" "}
              {userData.role === "provider"
                ? `Dr. ${userData.firstName}`
                : userData.firstName}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {userData.role === "provider"
                ? "Stay on top of your test orders and patient results."
                : "Easily manage your test orders, track progress, and access health insights."}
            </motion.p>
          </motion.div>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
            <motion.img
              key={`thumbprint-${i}`}
              src={`${process.env.PUBLIC_URL}/assets/thumbprint.png`}
              alt="Thumbprint"
              className={`thumbprint-image thumbprint-image-${Math.min(i, 6)}`}
              variants={thumbprintVariants}
              custom={i}
              initial="hidden"
              animate="visible"
            />
          ))}
        </motion.div>
        <motion.div className="quick-actions-card" variants={itemVariants}>
          <motion.div
            className="quick-actions-header"
            variants={headingVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/quick-actions-icon.png`}
              alt="Quick Actions"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Quick actions
            </motion.h2>
          </motion.div>
          <motion.button
            className="quick-action-button"
            onClick={() => navigate("/order-a-test")}
            whileHover="hover"
            whileTap="tap"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.8406 22.0022H8.16059C3.97059 22.0022 3.14059 19.4722 4.50059 16.3922L5.94059 13.1222C5.94059 13.1222 9.00059 13.0022 12.0006 14.0022C15.0006 15.0022 17.8306 13.1122 17.8306 13.1122L18.0206 12.9922L19.5106 16.4022C20.8506 19.4822 19.9706 22.0022 15.8406 22.0022Z"
                fill="#949A9C"
              />
              <path
                d="M15.4403 6.74H15.2803L17.4103 11.6L17.0003 11.86C16.9803 11.87 14.7203 13.32 12.4703 12.58C10.1203 11.79 7.76031 11.65 6.60031 11.63L8.74031 6.74H8.44031C7.79031 6.74 7.19031 6.48 6.76031 6.05C6.33031 5.62 6.07031 5.02 6.07031 4.37C6.07031 3.07 7.13031 2 8.44031 2H15.5503C16.2103 2 16.8003 2.27 17.2303 2.7C17.7903 3.26 18.0803 4.08 17.8603 4.95C17.6003 6.03 16.5603 6.74 15.4403 6.74Z"
                fill="#949A9C"
              />
            </svg>
            Order a test
          </motion.button>
          <motion.button
            className="quick-action-button"
            onClick={() =>
              navigate(
                userData.role === "provider"
                  ? "/contact-form"
                  : "/medical-intake-form"
              )
            }
            whileHover="hover"
            whileTap="tap"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {userData.role === "provider" ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM15.75 15.75H8.25C7.84 15.75 7.5 15.41 7.5 15C7.5 14.59 7.84 14.25 8.25 14.25H15.75C16.16 14.25 16.5 14.59 16.5 15C16.5 15.41 16.16 15.75 15.75 15.75ZM15.75 9.75H8.25C7.84 9.75 7.5 9.41 7.5 9C7.5 8.59 7.84 8.25 8.25 8.25H15.75C16.16 8.25 16.5 8.59 16.5 9C16.5 9.41 16.16 9.75 15.75 9.75Z"
                  fill="#949A9C"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.19 2H7.81C4.17 2 2 4.17 2 7.81V16.18C2 19.83 4.17 22 7.81 22H16.18C19.82 22 21.99 19.83 21.99 16.19V7.81C22 4.17 19.83 2 16.19 2ZM15.75 15.75H8.25C7.84 15.75 7.5 15.41 7.5 15C7.5 14.59 7.84 14.25 8.25 14.25H15.75C16.16 14.25 16.5 14.59 16.5 15C16.5 15.41 16.16 15.75 15.75 15.75ZM15.75 9.75H8.25C7.84 9.75 7.5 9.41 7.5 9C7.5 8.59 7.84 8.25 8.25 8.25H15.75C16.16 8.25 16.5 8.59 16.5 9C16.5 9.41 16.16 9.75 15.75 9.75Z"
                  fill="#949A9C"
                />
              </svg>
            )}{" "}
            {userData.role === "provider"
              ? "Fill contact form"
              : "Fill medical intake form"}
          </motion.button>
        </motion.div>
      </motion.div>
      <motion.div
        className="metrics-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        transition={{ delayChildren: 0.3, staggerChildren: 0.1 }}
      >
        <motion.div
          className="metrics-header"
          variants={headingVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.img
            src={`${process.env.PUBLIC_URL}/assets/metrics-icon.png`}
            alt="Metrics"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          />
          <motion.h2
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Metrics
          </motion.h2>
        </motion.div>
        {isLoading ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <LoadingDashboard />
          </motion.div>
        ) : (
          <motion.div
            className="metrics-cards"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="metric-card"
              variants={itemVariants}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.6064 2.21C12.2464 1.93 11.7464 1.93 11.3864 2.21C9.48641 3.66 3.87637 8.39 3.90637 13.9C3.90637 18.36 7.53641 22 12.0064 22C16.4764 22 20.1064 18.37 20.1064 13.91C20.1164 8.48 14.4964 3.67 12.6064 2.21Z"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-miterlimit="10"
                />
                <path
                  d="M12 2V22"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 18.9587L19.7 15.2188"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 13.9628L19.37 10.3828"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M12 8.95781L17.03 6.50781"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span>Ordered tests</span>
              <motion.span
                className="metric-value"
                variants={metricCountVariants}
                initial="hidden"
                animate="visible"
              >
                {orderedTests}
              </motion.span>
            </motion.div>
            <motion.div
              className="metric-card"
              variants={itemVariants}
              whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20 8.25V18C20 21 18.21 22 16 22H8C5.79 22 4 21 4 18V8.25C4 5 5.79 4.25 8 4.25C8 4.87 8.24997 5.43 8.65997 5.84C9.06997 6.25 9.63 6.5 10.25 6.5H13.75C14.99 6.5 16 5.49 16 4.25C18.21 4.25 20 5 20 8.25Z"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16 4.25C16 5.49 14.99 6.5 13.75 6.5H10.25C9.63 6.5 9.06997 6.25 8.65997 5.84C8.24997 5.43 8 4.87 8 4.25C8 3.01 9.01 2 10.25 2H13.75C14.37 2 14.93 2.25 15.34 2.66C15.75 3.07 16 3.63 16 4.25Z"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 13H12"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8 17H16"
                  stroke="#3A3F6A"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <span>Pending results</span>
              <motion.span
                className="metric-value"
                variants={metricCountVariants}
                initial="hidden"
                animate="visible"
              >
                {pendingResults}
              </motion.span>
            </motion.div>
            {userData.role === "provider" && (
              <motion.div
                className="metric-card"
                variants={itemVariants}
                whileHover={{ scale: 1.03, transition: { duration: 0.2 } }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.1625 10.87C9.0625 10.86 8.9425 10.86 8.8325 10.87C6.4525 10.79 4.5625 8.84 4.5625 6.44C4.5625 3.99 6.5425 2 9.0025 2C11.4525 2 13.4425 3.99 13.4425 6.44C13.4325 8.84 11.5425 10.79 9.1625 10.87Z"
                    stroke="#3A3F6A"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.4112 4C18.3512 4 19.9112 5.57 19.9112 7.5C19.9112 9.39 18.4113 10.93 16.5413 11C16.4613 10.99 16.3713 10.99 16.2812 11"
                    stroke="#3A3F6A"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M4.15875 14.56C1.73875 16.18 1.73875 18.82 4.15875 20.43C6.90875 22.27 11.4188 22.27 14.1688 20.43C16.5888 18.81 16.5888 16.17 14.1688 14.56C11.4288 12.73 6.91875 12.73 4.15875 14.56Z"
                    stroke="#3A3F6A"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M18.3438 20C19.0638 19.85 19.7437 19.56 20.3037 19.13C21.8637 17.96 21.8637 16.03 20.3037 14.86C19.7537 14.44 19.0838 14.16 18.3738 14"
                    stroke="#3A3F6A"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <span>Patients</span>
                <motion.span
                  className="metric-value"
                  variants={metricCountVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {totalPatients}
                </motion.span>
              </motion.div>
            )}
          </motion.div>
        )}
      </motion.div>
      <motion.div
        className="orders-overview-container"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div className="orders-overview-header" variants={itemVariants}>
          <motion.div
            className="orders-overview-title"
            variants={headingVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.img
              className="orders-overview-icon"
              src={`${process.env.PUBLIC_URL}/assets/orders-overview-icon.png`}
              alt="Orders Overview"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            />
            <motion.h2
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              Orders overview
            </motion.h2>
          </motion.div>
          <motion.button
            className="view-all-button"
            onClick={() => navigate("/my-orders")}
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            View all orders
          </motion.button>
        </motion.div>
        {isLoadingOrders ? (
          <motion.div
            className="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <LoadingDashboard />
          </motion.div>
        ) : ordersError ? (
          <motion.div
            className="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            Error: {ordersError}
          </motion.div>
        ) : orders.length > 0 ? (
          <motion.div
            className="orders-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {recentOrders.map((order, index) => (
              <motion.div
                key={order.orderId}
                className="order-card"
                variants={orderCardVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                onMouseEnter={(e) => e.currentTarget.classList.add("hover")}
                onMouseLeave={(e) => e.currentTarget.classList.remove("hover")}
                custom={index}
                onClick={() => navigate(`/my-order/${order.orderId}`)}
              >
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1 + index * 0.05 }}
                >
                  Order ID: {order.orderId}
                </motion.span>
                {userData.role === "provider" && (
                  <motion.span
                    className="patient-name"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                  >
                    {order.patientFirstName} {order.patientLastName}
                  </motion.span>
                )}
                <motion.span
                  className="test-name"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 + index * 0.05 }}
                >
                  {order.testName}
                </motion.span>
                <motion.span
                  className={`status ${statusClasses[order.status]}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 + index * 0.05 }}
                >
                  <Menu size={16} /> {order.status}
                </motion.span>
                <motion.span
                  className="order-date"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 + index * 0.05 }}
                >
                  {order.orderDate}
                </motion.span>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="no-orders"
            variants={fadeIn}
            initial="initial"
            animate="animate"
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/order-logo.svg`}
              alt="No orders"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              No orders yet.
            </motion.p>
          </motion.div>
        )}
      </motion.div>
      {userData.role === "provider" && (
        <motion.div
          className="patients-container"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="patients-header" variants={itemVariants}>
            <motion.div
              className="patients-title"
              variants={headingVariants}
              initial="hidden"
              animate="visible"
            >
              <motion.img
                className="patients-icon"
                src={`${process.env.PUBLIC_URL}/assets/patient-icon.png`}
                alt="Patients"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              />
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                Patients
              </motion.h2>
            </motion.div>
            <motion.div
              className="view-add-buttons"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <motion.button
                className="view-all-button"
                onClick={() => navigate("/manage-patients")}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                View all patients
              </motion.button>
              <motion.button
                className="add-patient-button"
                onClick={() => navigate("/add-patient")}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                Add new patient
              </motion.button>
            </motion.div>
          </motion.div>
          {isLoadingPatients ? (
            <motion.div
              className="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <LoadingDashboard />
            </motion.div>
          ) : patientsError ? (
            <motion.div
              className="error"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              Error: {patientsError}
            </motion.div>
          ) : patients.length > 0 ? (
            <motion.div
              className="patients-grid"
              variants={fadeIn}
              initial="initial"
              animate="animate"
            >
              <motion.div
                className="table-header"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <span>Name</span>
                <span>Patient ID</span>
                <span>Tests ordered</span>
              </motion.div>
              <motion.div
                className="table-body"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {recentPatients.map((patient, index) => (
                  <motion.div
                    key={patient.id}
                    className="table-row"
                    variants={patientRowVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover="hover"
                    custom={index}
                  >
                    <div className="patient-info">
                      <motion.div
                        className="profile-pic"
                        variants={profilePicVariants}
                        whileHover="hover"
                      >
                        {patient.firstName.charAt(0)}
                      </motion.div>
                      <div className="name-email-container">
                        <motion.span
                          className="patient-name-value"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 + index * 0.05 }}
                        >
                          {patient.firstName} {patient.lastName}
                        </motion.span>
                        <motion.span
                          className="patient-email-value"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.2 + index * 0.05 }}
                        >
                          {patient.email}
                        </motion.span>
                      </div>
                    </div>
                    <motion.span
                      className="patientid"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                    >
                      {patient.id}
                    </motion.span>
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 + index * 0.05 }}
                    >
                      {patient.testsOrdered === 0
                        ? "None"
                        : patient.testsOrdered}
                    </motion.span>
                    <motion.button
                      className="order-test-button"
                      onClick={() => navigate("/order-a-test")}
                      whileHover={{ color: "#222952" }}
                      whileTap={{ scale: 0.95 }}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 + index * 0.05 }}
                    >
                      Order a test
                    </motion.button>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          ) : (
            <motion.div
              className="no-orders"
              variants={fadeIn}
              initial="initial"
              animate="animate"
            >
              <motion.img
                src={`${process.env.PUBLIC_URL}/assets/no-patients.png`}
                alt="No patients"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: "spring", stiffness: 200 }}
              />
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                No patients yet.
              </motion.p>
            </motion.div>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default Dashboard;
